"use client";

import dynamic from "next/dynamic";
import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  createStartupEvent,
  createStartupEventPayment,
} from "@/services/api/StartupEvent";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { upload } from "@/services/api/Career";
import { loadStripe } from "@stripe/stripe-js";
import {
  getLatestSessionIdOnStartupEvent,
  getSessionBySessionId,
} from "@/services/api/Stripe";
import { useSearchParams } from "next/navigation";
import toast from "react-hot-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import axios from "axios";
import Spinner from "@/components/Spinner";
import useAuth from "@/hooks/auth";

// Dynamically import components with ssr: false
const DatePicker = dynamic(
  () => import("@/components/ui/date-picker").then((mod) => mod.DatePicker),
  { ssr: false }
);
const MapPicker = dynamic(
  () => import("@/components/map-picker").then((mod) => mod.MapPicker),
  { ssr: false }
);

export default function CreateStartupEvent() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    name: "",
    city: "",
    country: "",
    location: "",
    date: null,
    website: "",
    short_description: "",
    organizer: "",
    lat: null,
    lng: null,
    image_url: "",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [preventSearch, setPreventSearch] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isEditingVenue, setIsEditingVenue] = useState(true);

  const [errors, setErrors] = useState({});

  // Add new loading states near other state declarations
  const [isCreating, setIsCreating] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const { user } = useAuth({ middleware: "auth" });

  const extractCityAndCountry = (location) => {
    const parts = location.split(",");
    let city = "";
    let country = "";

    if (parts.length >= 3) {
      city = parts[parts.length - 3].trim(); // Consider city at the third position from the end
      country = parts[parts.length - 1].trim(); // Country is usually the last part
    } else if (parts.length >= 2) {
      city = parts[parts.length - 2].trim(); // Fallback city position
      country = parts[parts.length - 1].trim(); // Fallback country position
    }

    // Additional checks to ensure city is not a postal code or other non-city information
    if (!isNaN(city) || city.match(/^\d+$/)) {
      city = ""; // Clear if it's a number (likely postal code)
    }

    return { city, country };
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchLocation = useCallback(async (query) => {
    try {
      const response = await fetch(
        `https://us1.locationiq.com/v1/search.php?key=${
          process.env.NEXT_PUBLIC_LOCATIONIQ_API_KEY
        }&q=${encodeURIComponent(query)}&format=json`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setSearchResults(data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching location:", error);
      setSearchResults([]);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery && !preventSearch) {
      searchLocation(debouncedSearchQuery);
    } else {
      setSearchResults([]);
      setPreventSearch(false); // Reset the flag
    }
  }, [debouncedSearchQuery, searchLocation, preventSearch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPreventSearch(false); // Allow searching when user types
  };

  const handleResultSelect = (result) => {
    const { lat, lon, display_name } = result;
    const { city, country } = extractCityAndCountry(display_name);

    setFormData({
      ...formData,
      lat: parseFloat(lat),
      lng: parseFloat(lon),
      location: display_name,
      city,
      country,
    });
    setSelectedVenue(display_name);
    setSearchQuery("");
    setSearchResults([]);
    setShowMap(true);
    setPreventSearch(true);
    setIsEditingVenue(false);
  };

  const handleChangeVenue = () => {
    setIsEditingVenue(true);
    setSearchQuery("");
    setSelectedVenue(null);
  };

  const handleMapLocationSelect = ({ lat, lng }) => {
    setFormData({
      ...formData,
      lat,
      lng,
    });
    // Optionally, you can update the search query with the new coordinates
    setSearchQuery(`${lat}, ${lng}`);
  };

  const uploadFile = async (file, folder) => {
    setIsUploadingImage(true);
    const formData = new FormData();
    formData.append("filepond", file);
    formData.append("folder", folder);

    try {
      const response = await upload(formData);
      return response.data.url;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      const url = await uploadFile(file, "uploads/startup_events");
      setImageUrl(url);
    }
  };

  const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
  );

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'name', 'city', 'country', 'location', 'date', 'short_description', 'organizer'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (!formData.lat || !formData.lng) {
      newErrors.location = 'Please select a valid location on the map';
    }

    if (!imageUrl && !imageFile) {
      newErrors.image = 'Please upload an event image';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePay = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const eventData = {
        ...formData,
        image_url: imageUrl,
      };

      const {
        data: { id: sessionId },
      } = await createStartupEventPayment({
        initial_input: JSON.stringify(eventData),
      });

      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        console.error("Error:", error);
        toast.error("Payment processing failed. Please try again.");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Payment processing failed. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      const eventData = {
        ...formData,
        image_url: imageUrl || formData.image_url,
      };

      await createStartupEvent({ ...eventData, session_id: sessionId });
      router.push("/startup-events");
    } catch (error) {
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getSessionDataForStartupEvent = useCallback(async () => {
    try {
      const { data } = await getLatestSessionIdOnStartupEvent();
      if (data && data.session_id) {
        setSessionId(data.session_id);
        const response = await getSessionBySessionId({
          session_id: data.session_id,
        });
        if (response && response.data) {
          setSessionData(response.data);
          const parsedInput = JSON.parse(response.data.initial_input);
          setFormData({ ...parsedInput, date: new Date(parsedInput.date) });
          setShowMap(true);
          setSearchQuery(parsedInput.location);
          setSelectedVenue(parsedInput.location);
          setIsEditingVenue(false);
          setImagePreview(parsedInput.image_url);
          setImageUrl(parsedInput.image_url)
        }
      }
    } catch (error) {
      console.log("Error fetching session data:", error?.response?.data?.message);
    }
  }, []);

  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const searchParams = useSearchParams();

  useEffect(() => {
    const fetchSessionData = async () => {
      const session_id = searchParams.get("session_id");

      if (session_id) {
        setSessionId(session_id);

        try {
          const response = await getSessionBySessionId({ session_id });
          if (response && response.data) {
            setSessionData(response.data);
            const parsedInput = JSON.parse(response.data.initial_input);
            setFormData({ ...parsedInput, date: new Date(parsedInput.date) });
            setShowMap(true);
            setSearchQuery(parsedInput.location);
            setSelectedVenue(parsedInput.location);
            setIsEditingVenue(false);
            setImagePreview(parsedInput.image_url);
            setImageUrl(parsedInput.image_url)
  
            // Auto-submit if payment is completed
            if (response.data.stripe_data.payment_status === "paid") {
              const eventData = {
                ...parsedInput,
                image_url: parsedInput.image_url,
              };
              const response = await createStartupEvent({ ...eventData, session_id });
              router.push(`/startup-events/${response.data.data.id}`);
            }
          }
        } catch (error) {
          console.log("Error fetching session data:", error?.response?.data?.message);
        }
      } else {
        getSessionDataForStartupEvent();
      }
    };

    fetchSessionData();
  }, [searchParams, getSessionDataForStartupEvent]);

  return (
    <Layout
      title='Create Startup Event'
      description='Create a new startup event and share it with the community'
    >
      <div className='container mx-auto p-8 max-w-3xl'>
        <Card>
          <CardHeader>
            <CardTitle className='text-3xl font-bold text-center'>
              Create Startup Event
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessionData && sessionData.stripe_data && sessionData.stripe_data.payment_status === "paid" ? (
               <Alert
               variant='default'
               className='mb-6 bg-green-50 border-green-200'
             >
               <div className='flex items-start'>
                 <AlertCircle className='h-5 w-5 text-green-500 mt-0.5 mr-3' />
                 <div>
                   <AlertTitle className='text-green-700 font-semibold mb-1'>
                     Payment Completed
                   </AlertTitle>
                   <AlertDescription className='text-green-600'>
                     <p className='mb-2'>
                       Your payment has been processed successfully. You can now create your startup event.
                     </p>
                   </AlertDescription>
                 </div>
               </div>
             </Alert>)
             : sessionData && sessionData.stripe_data && sessionData.stripe_data.payment_status === "unpaid" ? (
              <Alert
                variant='default'
                className='mb-6 bg-red-50 border-red-200'
              >
                <div className='flex items-start'>
                  <AlertCircle className='h-5 w-5 text-red-500 mt-0.5 mr-3' />
                  <div>
                    <AlertTitle className='text-red-700 font-semibold mb-1'>
                      Payment Required
                    </AlertTitle>
                    <AlertDescription className='text-red-600'>
                      <p className='mb-2'>
                        Please complete the payment to create your event.
                      </p>
                      <p className='font-medium'>
                        Amount due:{" "}
                        {(sessionData.stripe_data.amount_total / 100).toFixed(
                          2
                        )}{" "}
                        {sessionData.stripe_data.currency.toUpperCase()}
                      </p>
                    </AlertDescription>
                    <Button
                      onClick={() =>
                        (window.location.href = sessionData.stripe_data.url)
                      }
                      className='mt-3 bg-red-600 hover:bg-red-700 text-white'
                    >
                      Complete Payment
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : !sessionData && (
              <Alert
                variant='default'
                className='mb-6 bg-blue-50 border-blue-200'
              >
                <div className='flex items-start'>
                  <AlertCircle className='h-5 w-5 text-blue-500 mt-0.5 mr-3' />
                  <div>
                    <AlertTitle className='text-blue-700 font-semibold mb-1'>
                      Event Creation Fee
                    </AlertTitle>
                    <AlertDescription className='text-blue-600'>
                      <p className='mb-2'>
                        To create your event, a fee is required.
                      </p>
                      <p className='font-medium'>
                        Amount due: 50 USD
                      </p>
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            )}

            <div className='space-y-6'>
              <div className='space-y-2'>
                <Label htmlFor='name'>Event Name</Label>
                <Input
                  id='name'
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder='Enter event name'
                  required
                />
                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
              </div>
              <Separator className='my-6' />
              <div className='space-y-4'>
                <Label>Venue</Label>
                <div className='relative z-20'>
                  {isEditingVenue ? (
                    <>
                      <Input
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder='Search for a location'
                        className='mb-2'
                      />
                      {searchResults.length > 0 && !preventSearch && (
                        <div className='absolute z-30 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto'>
                          {searchResults.map((result, index) => (
                            <div
                              key={index}
                              className='cursor-pointer hover:bg-gray-100 p-2 text-gray-800'
                              onClick={() => handleResultSelect(result)}
                            >
                              {result.display_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className='flex items-center justify-between bg-gray-100 p-2 rounded'>
                      <span className='text-gray-800'>{selectedVenue}</span>
                      <Button
                        type='button'
                        onClick={handleChangeVenue}
                        variant='outline'
                        size='sm'
                      >
                        Change Venue
                      </Button>
                    </div>
                  )}
                </div>
                <div className='relative z-10'>
                  {showMap && (
                    <MapPicker
                      onLocationSelect={handleMapLocationSelect}
                      initialLat={formData.lat}
                      initialLng={formData.lng}
                    />
                  )}
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>City</Label>
                    <Input
                      id='city'
                      name='city'
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder='City'
                      required
                    />
                    {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='country'>Country</Label>
                    <Input
                      id='country'
                      name='country'
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder='Country'
                      required
                    />
                    {errors.country && <p className="text-red-500 text-sm">{errors.country}</p>}
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='location'>Address</Label>
                  <Input
                    id='location'
                    name='location'
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder='Full address'
                    required
                  />
                  {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}
                </div>
              </div>
              <Separator className='my-6' />
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='date'>Event Date</Label>
                  <DatePicker
                    id='date'
                    selected={formData.date}
                    onChange={handleDateChange}
                    placeholderText='Select Date'
                    required
                    className='w-full'
                  />
                  {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='website'>Website</Label>
                  <Input
                    id='website'
                    name='website'
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder='Event website URL'
                  />
                  {errors.website && <p className="text-red-500 text-sm">{errors.website}</p>}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='short_description'>Description</Label>
                  <Textarea
                    id='short_description'
                    name='short_description'
                    value={formData.short_description}
                    onChange={handleInputChange}
                    placeholder='Description of the event'
                    required
                  />
                  {errors.short_description && <p className="text-red-500 text-sm">{errors.short_description}</p>}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='organizer'>Organizer</Label>
                  <Input
                    id='organizer'
                    name='organizer'
                    value={formData.organizer}
                    onChange={handleInputChange}
                    placeholder="Organizer's name"
                    required
                  />
                  {errors.organizer && <p className="text-red-500 text-sm">{errors.organizer}</p>}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='image'>Event Image</Label>
                  <div className='flex items-center space-x-4'>
                    <Button
                      type="button"
                      onClick={() => document.getElementById('image-upload').click()}
                      variant="outline"
                    >
                      Choose Image
                    </Button>
                    {imageFile && (
                      <span className="text-sm text-gray-500">{imageFile.name}</span>
                    )}
                  </div>
                  <input
                    id='image-upload'
                    type='file'
                    onChange={handleImageFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                  {imagePreview && (
                    <div className="mt-4 relative">
                      <img
                        src={imagePreview}
                        alt='Event Image Preview'
                        className='max-w-[300px] w-full h-[200px] object-cover rounded-lg shadow-md'
                      />
                      {isUploadingImage && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                          <Spinner color="white" />
                        </div>
                      )}
                    </div>
                  )}
                  {errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
                </div>
              </div>
              <Button
                onClick={
                  sessionData &&
                  sessionData.stripe_data.payment_status === "paid"
                    ? handleSubmit
                    : handlePay
                }
                type='submit'
                disabled={
                  isUploadingImage ||
                  isCreating ||
                  isProcessingPayment ||
                  (sessionData &&
                    sessionData.stripe_data.payment_status === "unpaid")
                }
              >
                {isCreating ? (
                  "Creating Event..."
                ) : isProcessingPayment ? (
                  "Processing Payment..."
                ) : isUploadingImage ? (
                  "Uploading Image..."
                ) : !sessionData ||
                  (sessionData &&
                    sessionData.stripe_data.payment_status === "unpaid") ? (
                  "Complete Payment to Create Event"
                ) : (
                  "Create Event"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
